<template>
  <section id="gallery" class="gallery">
    <h2>{{ $t('gallery.title') }}</h2>

    <div class="carousel-wrapper">
      <button
        v-if="totalSlides > 1"
        type="button"
        class="carousel-control left"
        :aria-label="$t('gallery.previous')"
        @click="prevSlide"
      >
        ❮
      </button>

      <div class="carousel-container">
        <div
          class="carousel-track-container"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        >
          <div class="carousel-track">
            <div
              v-for="(image, index) in visibleImages"
              :key="image.id"
              class="carousel-slide"
            >
              <button
                type="button"
                class="image-button"
                :aria-label="$t('gallery.openImage', { index: visibleStart + index + 1 })"
                @click="openModal(image)"
              >
                <img
                  :src="image.src"
                  :srcset="image.srcSet"
                  sizes="(max-width: 767px) calc(100vw - 70px), 27vw"
                  :alt="$t('gallery.imageAlt', { index: visibleStart + index + 1 })"
                  loading="lazy"
                  decoding="async"
                />
                <span class="overlay" aria-hidden="true">
                  <span>{{ $t('gallery.content.button.postlink') }}</span>
                  <img
                    src="@/assets/icons/gallery-expand.svg"
                    alt=""
                    class="finger-icon"
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        v-if="totalSlides > 1"
        type="button"
        class="carousel-control right"
        :aria-label="$t('gallery.next')"
        @click="nextSlide"
      >
        ❯
      </button>
    </div>

    <div
      v-if="modalImage"
      class="modal"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('gallery.dialogLabel')"
      @click.self="closeModal"
    >
      <div class="modal-content">
        <button
          ref="closeButton"
          type="button"
          class="modal-close"
          :aria-label="$t('gallery.close')"
          @click="closeModal"
        >
          &times;
        </button>
        <img
          :src="modalImage.full"
          :alt="$t('gallery.imageAlt', { index: modalImageIndex + 1 })"
          decoding="async"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { galleryImages } from '@/data/media';

const currentSlide = ref(0);
const startX = ref(0);
const viewportWidth = ref(window.innerWidth);
const modalImage = ref(null);
const closeButton = ref(null);
let previouslyFocused = null;

const slidesPerPage = computed(() => (viewportWidth.value < 768 ? 1 : 3));
const totalSlides = computed(() => Math.ceil(galleryImages.length / slidesPerPage.value));
const visibleStart = computed(() => currentSlide.value * slidesPerPage.value);
const visibleImages = computed(() => (
  galleryImages.slice(visibleStart.value, visibleStart.value + slidesPerPage.value)
));
const modalImageIndex = computed(() => galleryImages.indexOf(modalImage.value));

const prevSlide = () => {
  currentSlide.value = currentSlide.value > 0
    ? currentSlide.value - 1
    : totalSlides.value - 1;
};

const nextSlide = () => {
  currentSlide.value = currentSlide.value < totalSlides.value - 1
    ? currentSlide.value + 1
    : 0;
};

const handleTouchStart = (event) => {
  startX.value = event.touches[0].clientX;
};

const handleTouchEnd = (event) => {
  const endX = event.changedTouches[0].clientX;
  if (startX.value > endX + 50) nextSlide();
  if (startX.value < endX - 50) prevSlide();
};

const openModal = async (image) => {
  previouslyFocused = document.activeElement;
  modalImage.value = image;
  document.body.style.overflow = 'hidden';
  await nextTick();
  closeButton.value?.focus();
};

const closeModal = () => {
  modalImage.value = null;
  document.body.style.overflow = '';
  previouslyFocused?.focus();
};

const handleResize = () => {
  viewportWidth.value = window.innerWidth;
  currentSlide.value = 0;
};

const handleKeydown = (event) => {
  if (event.key === 'Escape' && modalImage.value) closeModal();
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});
</script>

<style scoped>
.gallery {
  padding: clamp(64px, 7vw, 96px) 20px;
  text-align: center;
  overflow: hidden;
  background: linear-gradient(180deg, #fff 0%, #fff9f6 100%);
}

h2 {
  margin-bottom: 36px;
}

.carousel-wrapper {
  position: relative;
  width: min(1100px, calc(100% - 48px));
  margin: 0 auto;
}

.carousel-container,
.carousel-track-container {
  width: 100%;
  overflow: hidden;
}

.carousel-control {
  position: absolute;
  top: 50%;
  z-index: 10;
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  color: white;
  background: rgba(36, 52, 71, 0.82);
  box-shadow: 0 8px 24px rgba(36, 52, 71, 0.18);
  cursor: pointer;
  transform: translateY(-50%);
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.carousel-control:hover {
  background: var(--primary-color);
  box-shadow: 0 10px 26px rgba(201, 52, 59, 0.25);
  transform: translateY(-50%) scale(1.1);
}

.carousel-control.left {
  left: -22px;
}

.carousel-control.right {
  right: -22px;
}

.carousel-track {
  display: flex;
  align-items: flex-start;
}

.carousel-slide {
  display: flex;
  flex: 0 0 33.333%;
  align-items: flex-start;
  justify-content: center;
  padding: 0 10px;
}

.image-button {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  padding: 0;
  border: 1px solid rgba(36, 52, 71, 0.08);
  border-radius: 16px;
  background: #ece8e5;
  box-shadow: 0 12px 32px rgba(36, 52, 71, 0.12);
  cursor: pointer;
  touch-action: pan-y;
}

.image-button > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  border-radius: inherit;
  color: white;
  background: rgba(28, 42, 57, 0.68);
  opacity: 0;
  transition: opacity 0.25s ease;
}

.overlay > span {
  padding: 7px 13px;
  border: 1px solid rgba(255, 255, 255, 0.52);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.image-button:hover .overlay,
.image-button:focus-visible .overlay {
  opacity: 1;
}

.image-button:hover > img,
.image-button:focus-visible > img {
  transform: scale(1.035);
}

.finger-icon {
  width: 22px;
  margin-top: 2px;
}

.modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  background: rgba(0, 0, 0, 0.85);
}

.modal-content {
  position: relative;
  max-width: 1200px;
  max-height: 90vh;
}

.modal-content img {
  display: block;
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 10px;
}

.modal-close {
  position: absolute;
  top: -18px;
  right: -18px;
  z-index: 1;
  display: flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  color: white;
  background: rgba(0, 0, 0, 0.75);
  cursor: pointer;
}

@media (max-width: 767px) {
  .carousel-wrapper {
    width: calc(100% - 24px);
  }

  .carousel-slide {
    flex-basis: 100%;
    padding: 0 6px;
  }

  .carousel-control {
    width: 38px;
    height: 38px;
  }

  .carousel-control.left {
    left: -12px;
  }

  .carousel-control.right {
    right: -12px;
  }

  .overlay {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .carousel-control,
  .overlay {
    transition: none;
  }
}
</style>
